import { isAttribute } from '../helpers/is-attribute';

const { useEffect } = wp.element;

/**
 * Custom hook for managing query filters in ResultsBlock.
 *
 * Registers and manages wp.hooks filters that modify the Elasticsearch query
 * based on block attributes. Handles cleanup on unmount and re-registration
 * when dependencies change.
 *
 * @param {Object}   params                                 Hook parameters.
 * @param {number}   params.itemPerPage                     Number of items per page.
 * @param {boolean}  params.showVariationsOnlyForAttributes Whether to show variations only when attribute filters are active.
 * @param {boolean}  params.isSearchingProducts             Whether searching for products.
 * @param {boolean}  params.promoteFeaturedContent          Whether to promote featured content in results.
 * @param {Function} params.setIsEmptyQuery                 Callback to set empty query state.
 * @param {Array}    params.defaultFilters                  Default filters array for queries.
 * @param {Array}    params.defaultFiltersNone              Default "none" filters for queries.
 */
const useQueryFilters = ( {
	itemPerPage,
	showVariationsOnlyForAttributes,
	isSearchingProducts,
	promoteFeaturedContent,
	setIsEmptyQuery,
	defaultFilters,
	defaultFiltersNone,
} ) => {
	/**
	 * Register the initial query setup filter.
	 * Priority: 1 (runs early in the filter chain)
	 */
	useEffect( () => {
		const querySetupCallback = query => {
			// Set the initial query for initial search.
			// We need to explicitly check for query.query being null here, otherwise each request will default to window.QalaElasticCore.query
			// regardless of search input fields's values. If we just check if it's empty, it means that clearing an input field (which means
			// query.query == "" will change the query to window.QalaElasticCore.query, which is the value in ?s URL param)
			if ( query.query === null && window.QalaElasticCore.query ) {
				query.query = window.QalaElasticCore.query;
			} else if ( ! query.query ) { // This just makes sure we don't have any type issues.
				query.query = '';
			}

			query.options = {
				page: {
					size: itemPerPage,
				},
				filters: { all: [ ...defaultFilters ] },
			};

			if ( ! query.options?.sort && promoteFeaturedContent ) {
				query.options.sort = [ { is_featured: 'desc' } ];
			} else if ( promoteFeaturedContent ) {
				if ( ! Array.isArray( query.options.sort ) ) {
					query.options.sort = [ query.options.sort ];
				}
				query.options.sort = [ { is_featured: 'desc' }, ...query.options.sort ];
			}

			return query;
		};

		// We need to make sure to fallback to score
		const fallbackScoreCallback = query => {
			if ( ! Array.isArray( query.options?.sort ) ) {
				return query;
			}

			// AppSearch errors out on duplicate keys in the sort array, so keep only the first entry per key.
			const seenKeys = new Set();

			query.options.sort = [ ...query.options.sort, { _score: 'desc' } ].filter( sortEntry => {
				const sortKey = Object.keys( sortEntry )[ 0 ];

				if ( seenKeys.has( sortKey ) ) {
					return false;
				}

				seenKeys.add( sortKey );
				return true;
			} );

			return query;
		};

		wp.hooks.removeFilter( 'qalaElasticCore.filter.query', 'QalaElasticCoreResultsBlock', querySetupCallback, 1 );
		wp.hooks.addFilter( 'qalaElasticCore.filter.query', 'QalaElasticCoreResultsBlock', querySetupCallback, 1 );

		wp.hooks.removeFilter( 'qalaElasticCore.filter.query', 'QalaElasticCoreResultsBlock_fallback', fallbackScoreCallback, 1000 );
		wp.hooks.addFilter( 'qalaElasticCore.filter.query', 'QalaElasticCoreResultsBlock_fallback', fallbackScoreCallback, 1000 );

		return () => {
			wp.hooks.removeFilter( 'qalaElasticCore.filter.query', 'QalaElasticCoreResultsBlock', querySetupCallback, 1 );
			wp.hooks.removeFilter( 'qalaElasticCore.filter.query', 'QalaElasticCoreResultsBlock_fallback', fallbackScoreCallback, 1000 );
		};
	}, [ itemPerPage, defaultFilters, promoteFeaturedContent ] );

	/**
	 * Register the empty query detection filter.
	 * Priority: 99 (runs after most filters but before variations filter)
	 */
	useEffect( () => {
		const emptyQueryCallback = query => {
			if ( query.query ) {
				setIsEmptyQuery( false );
				return query;
			}

			const filters = query.options.filters;

			if ( filters.any || filters.none || filters.all.length > defaultFilters.length ) {
				setIsEmptyQuery( false );
				return query;
			}

			setIsEmptyQuery( true );
			return query;
		};

		wp.hooks.removeFilter( 'qalaElasticCore.filter.query', 'QalaElasticCoreResultsBlock_EmptyQuery', emptyQueryCallback, 99 );
		wp.hooks.addFilter( 'qalaElasticCore.filter.query', 'QalaElasticCoreResultsBlock_EmptyQuery', emptyQueryCallback, 99 );

		return () => {
			wp.hooks.removeFilter( 'qalaElasticCore.filter.query', 'QalaElasticCoreResultsBlock_EmptyQuery', emptyQueryCallback, 99 );
		};
	}, [ setIsEmptyQuery, defaultFilters ] );

	/**
	 * Register the variations filter (for WooCommerce products).
	 * Priority: 110 (runs late, after empty query check)
	 */
	useEffect( () => {
		if ( ! isSearchingProducts || ! showVariationsOnlyForAttributes ) {
			return;
		}

		const variationsCallback = query => {
			// Check if there's any attribute filters in current query.
			const hasAnyAttributeFilters = [ ...( query.options.filters?.all || [] ), ...( query.options.filters?.any || [] ) ].some( filter => {
				const filterKey = Object.keys( filter )[ 0 ];
				return isAttribute( filterKey );
			} );

			if ( hasAnyAttributeFilters ) {
				return query;
			}

			// Exclude product variations from the query (since the query doesn't filter by any attributes).
			const noneFilters = query.options.filters?.none;

			if ( ! noneFilters ) {
				query.options.filters.none = [ ...defaultFiltersNone ];
			} else {
				query.options.filters.none = [ ...noneFilters, ...defaultFiltersNone ];
			}

			query.options.showVariationsOnlyForAttributes = showVariationsOnlyForAttributes;

			return query;
		};

		wp.hooks.removeFilter( 'qalaElasticCore.filter.query', 'QalaElasticCoreResultsBlock_Variations', variationsCallback, 110 );
		wp.hooks.addFilter( 'qalaElasticCore.filter.query', 'QalaElasticCoreResultsBlock_Variations', variationsCallback, 110 );

		return () => {
			wp.hooks.removeFilter( 'qalaElasticCore.filter.query', 'QalaElasticCoreResultsBlock_Variations', variationsCallback, 110 );
		};
	}, [ isSearchingProducts, showVariationsOnlyForAttributes, defaultFiltersNone ] );
};

export default useQueryFilters;
